// AuditLogPage.tsx — Section IX: Audit Log.
//
// "Every admin mutation. Append-only. Sorted newest first."
// (Lore-approved AU-03)
//
// Wire: admin_get_audit_log_filtered(actionType, sinceTimestamp, limit).
// CSV export: client-side, downloads current filtered view.
// Empty state: "No admin actions logged yet. Audit log populates as you operate."
//
// Pagination: load-more pattern (50 per page, increments limit on demand).

import React, { useState } from 'react';
import type { Identity } from '@dfinity/agent';
import type { AdminAction } from '../lib/adminAgent';
import { useAuditLog, AUDIT_ACTION_TYPES, type AuditActionFilter } from '../hooks/useAuditLog';

interface AuditLogPageProps {
  identity: Identity | null;
}

// Format nanosecond ICP timestamp as readable date
function formatTimestamp(ns: bigint): string {
  const ms = Number(ns / 1_000_000n);
  const d = new Date(ms);
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '') + ' UTC';
}

function truncatePrincipal(p: string): string {
  if (p.length <= 16) return p;
  return `${p.slice(0, 8)}…${p.slice(-5)}`;
}

function AuditRow({ entry }: { entry: AdminAction }): React.ReactElement {
  const ts = formatTimestamp(entry.timestamp);
  const caller = truncatePrincipal(entry.caller.toText());
  const target =
    entry.targetPrincipal.length > 0
      ? truncatePrincipal(entry.targetPrincipal[0]!.toText())
      : '—';

  return (
    <tr>
      <td><span className="mono">{ts}</span></td>
      <td><span className="mono">{caller}</span></td>
      <td><span className="pill">{entry.actionType}</span></td>
      <td><span className="mono">{target}</span></td>
      <td>{entry.details}</td>
    </tr>
  );
}

const DATE_RANGE_OPTIONS = [
  { label: 'All time', value: '' },
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
] as const;

type DateRange = '' | '24h' | '7d' | '30d';

function dateRangeToNs(range: DateRange): bigint | null {
  const now = BigInt(Date.now()) * 1_000_000n;
  if (range === '24h') return now - 86_400_000_000_000n;
  if (range === '7d') return now - 7n * 86_400_000_000_000n;
  if (range === '30d') return now - 30n * 86_400_000_000_000n;
  return null;
}

export default function AuditLogPage({ identity }: AuditLogPageProps): React.ReactElement {
  const { entries, isLoading, error, filters, setFilters, refresh, exportCsv } = useAuditLog(identity);
  const [dateRange, setDateRange] = useState<DateRange>('');

  const handleActionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ actionType: e.target.value as AuditActionFilter });
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const range = e.target.value as DateRange;
    setDateRange(range);
    setFilters({ sinceTimestamp: dateRangeToNs(range) });
  };

  const handleLoadMore = () => {
    setFilters({ limit: filters.limit + 50 });
  };

  return (
    <section className="section" id="audit" aria-labelledby="audit-title">
      <header className="section-header">
        <span className="section-eyebrow">Section IX</span>
        <h2 className="section-title" id="audit-title">Audit Log</h2>
        <p className="section-lede">
          Every admin mutation. Append-only. Sorted newest first.
        </p>
      </header>

      {/* Filter controls */}
      <div
        className="audit-filters"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--s-md)',
          marginBottom: 'var(--s-md)',
          alignItems: 'flex-end',
        }}
      >
        <div className="field" style={{ marginBottom: 0, minWidth: '200px' }}>
          <label className="field-label" htmlFor="audit-action-type">Action Type</label>
          <select
            id="audit-action-type"
            value={filters.actionType}
            onChange={handleActionTypeChange}
          >
            {AUDIT_ACTION_TYPES.map((t) => (
              <option key={t} value={t}>{t === 'all' ? 'All' : t}</option>
            ))}
          </select>
        </div>

        <div className="field" style={{ marginBottom: 0, minWidth: '180px' }}>
          <label className="field-label" htmlFor="audit-date-range">Date Range</label>
          <select
            id="audit-date-range"
            value={dateRange}
            onChange={handleDateRangeChange}
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 'var(--s-sm)', alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={refresh} disabled={isLoading}>
            {isLoading ? 'Loading' : 'Refresh'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={exportCsv}
            disabled={entries.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      {error && <div className="inline-error">{error}</div>}

      {entries.length === 0 && !isLoading ? (
        <div className="empty-state">
          <p className="empty-state-text">
            No admin actions logged yet. Audit log populates as you operate.
          </p>
        </div>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Caller</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <AuditRow key={`${String(entry.timestamp)}-${idx}`} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>

          <p className="card-caption" style={{ marginTop: 'var(--s-md)' }}>
            Older entries paginate below in production. The full log is exportable as CSV.
          </p>

          {entries.length >= filters.limit && (
            <button
              className="btn btn-secondary"
              onClick={handleLoadMore}
              disabled={isLoading}
              style={{ marginTop: 'var(--s-md)' }}
            >
              Load More
            </button>
          )}
        </>
      )}
    </section>
  );
}
